import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'

import { Upload, CheckCircle2, Clock, AlertCircle, Loader2 } from 'lucide-react'
import { useState, useEffect, useRef } from 'react'
import { toast } from '@/components/ui/sonner'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from '@/store'
import { fetchDocuments, uploadDocument } from '@/redux/documentsSlice'

interface DocumentsSettingsProps {
  athlete: any
}

// Fixed document types we expect
const EXPECTED_DOCUMENTS = [
  'Government ID',
  'W-9 Tax Form',
  'Proof of Enrollment',
  'Other Documents',
]

const DocumentsSettings = ({ athlete }: DocumentsSettingsProps) => {
  const dispatch = useDispatch<AppDispatch>()
  const { items, loading, uploading } = useSelector((state: RootState) => state.documents)

  const fileInputRef = useRef<HTMLInputElement>(null)
  const [activeUploadType, setActiveUploadType] = useState<string | null>(null)

  useEffect(() => {
    dispatch(fetchDocuments())
  }, [dispatch])

  // Merge expected documents with fetched items
  const displayDocuments = EXPECTED_DOCUMENTS.map((type) => {
    // Find latest document of this type
    // Items are sorted by date desc from backend, so find() gets the latest
    const existingDoc = items.find((doc) => doc.type === type)

    if (existingDoc) {
      return existingDoc
    }

    return {
      id: `placeholder-${type}`,
      type,
      name: '',
      status: 'not-uploaded' as const,
    }
  })

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file || !activeUploadType) return

    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large', { description: 'Please upload a file smaller than 10MB.' })
      return
    }

    try {
      await dispatch(uploadDocument({ file, documentType: activeUploadType })).unwrap()
      toast('Upload successful', {
        description: `${activeUploadType} has been uploaded successfully.`,
      })
    } catch (error: any) {
      toast.error('Upload failed', {
        description: error || 'Failed to upload document. Please try again.',
      })
    } finally {
      // Reset input
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
      setActiveUploadType(null)
    }
  }

  const triggerFileUpload = (documentType: string) => {
    setActiveUploadType(documentType)
    fileInputRef.current?.click()
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'verified':
      case 'approved':
        return <CheckCircle2 className="h-5 w-5 text-green-500" />
      case 'pending':
        return <Clock className="h-5 w-5 text-yellow-500" />
      case 'rejected':
        return <AlertCircle className="h-5 w-5 text-red-500" />
      default:
        return <AlertCircle className="h-5 w-5 text-gray-400" />
    }
  }

  const getStatusText = (status: string) => {
    switch (status) {
      case 'verified':
      case 'approved':
        return 'Verified'
      case 'pending':
        return 'Pending Review'
      case 'rejected':
        return 'Rejected'
      default:
        return 'Not Uploaded'
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'verified':
      case 'approved':
        return 'text-green-500'
      case 'pending':
        return 'text-yellow-500'
      case 'rejected':
        return 'text-red-500'
      default:
        return 'text-gray-400'
    }
  }

  return (
    <Card variant="dark" className="overflow-hidden">
      <CardHeader>
        <CardTitle className="text-white">Documents</CardTitle>
        <CardDescription className="text-gray-400">
          Upload and manage your verification documents
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <input
          type="file"
          ref={fileInputRef}
          className="hidden"
          onChange={handleFileSelect}
          accept=".pdf,.jpg,.jpeg,.png"
        />

        <div className="space-y-4">
          {displayDocuments.map((doc) => (
            <div
              key={doc.id}
              className="flex items-center justify-between gap-3 p-4 bg-white/5 rounded-lg border border-white/10"
            >
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <div className="h-4 w-4 rounded-full bg-[#C0FD72] border border-[#C0FD72] shrink-0" />
                <div className="min-w-0">
                  <span className="text-white font-medium">{doc.type}</span>
                  {doc.name && <p className="text-sm text-gray-400 mt-1 truncate">{doc.name}</p>}
                  {doc.rejectionReason && (
                    <p className="text-xs text-red-400 mt-1">Reason: {doc.rejectionReason}</p>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                <div className="flex items-center gap-1.5">
                  {getStatusIcon(doc.status)}
                  <span
                    className={`text-sm font-medium hidden sm:inline ${getStatusColor(doc.status)}`}
                  >
                    {getStatusText(doc.status)}
                  </span>
                </div>

                {doc.status === 'not-uploaded' || doc.status === 'rejected' ? (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => triggerFileUpload(doc.type)}
                    disabled={uploading[doc.type]}
                    className="bg-white/5 text-white border-white/10 hover:bg-white/10"
                  >
                    {uploading[doc.type] ? (
                      <Loader2 className="h-4 w-4 sm:mr-2 animate-spin" />
                    ) : (
                      <Upload className="h-4 w-4 sm:mr-2" />
                    )}
                    <span className="hidden sm:inline">Upload</span>
                  </Button>
                ) : null}
              </div>
            </div>
          ))}
        </div>

        <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
          <p className="text-sm text-blue-200">
            <strong>Note:</strong> All documents are stored securely. Verification typically takes
            1-2 business days.
          </p>
        </div>
      </CardContent>
    </Card>
  )
}

export default DocumentsSettings
